import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

const Analytics: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#003366', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/')}
            sx={{ 
              color: 'white', 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Home
          </Button>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 700, mb: 2, color: 'white' }}>
            Intelligence-Grade Analytics
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, color: 'white' }}>
            Real-Time Insights for Mission-Critical Operations
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.95rem', mb: 3, maxWidth: '800px', lineHeight: 1.6 }}>
            Advanced analytics dashboards with threat detection, emergency response coordination, and cross-agency intelligence sharing capabilities.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#003366',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Key Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Analytics Capabilities
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <DashboardIcon sx={{ fontSize: 40 }} />,
              title: 'Real-Time Dashboards',
              desc: 'Live operational dashboards with customizable widgets and role-based views'
            },
            {
              icon: <SpeedIcon sx={{ fontSize: 40 }} />,
              title: 'Threat Detection',
              desc: 'AI-powered anomaly detection and automated threat intelligence integration'
            },
            {
              icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
              title: 'Predictive Analytics',
              desc: 'Machine learning models for forecasting and trend analysis'
            },
            {
              icon: <BarChartIcon sx={{ fontSize: 40 }} />,
              title: 'Cross-Agency Intelligence',
              desc: 'Secure data sharing and collaborative analytics across federal agencies'
            }
          ].map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Box sx={{ color: '#003366', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Use Cases */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Mission-Critical Use Cases
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Emergency Response
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Coordinate multi-agency emergency operations with real-time situational awareness
                </Typography>
                <List dense>
                  {[
                    'Resource allocation tracking',
                    'Incident timeline visualization',
                    'Multi-agency coordination'
                  ].map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#003366', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Threat Intelligence
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Aggregate and analyze threat data from multiple sources
                </Typography>
                <List dense>
                  {[
                    'Automated threat correlation',
                    'Risk scoring and prioritization',
                    'Intelligence sharing protocols'
                  ].map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#003366', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Performance Monitoring
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Track KPIs and operational metrics across programs
                </Typography>
                <List dense>
                  {[
                    'Custom KPI dashboards',
                    'Automated reporting',
                    'Trend analysis and forecasting'
                  ].map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#003366', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Analytics Benefits
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Sub-second query performance',
                'Scalable to billions of records',
                'AI-powered insights and recommendations',
                'Mobile-responsive dashboards'
              ].map((item, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Secure data visualization',
                'Export to multiple formats',
                'Scheduled report distribution',
                'API access for custom integrations'
              ].map((item, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Card sx={{ p: 4, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#003366', fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.75rem' } }}>
              Unlock Intelligence-Grade Analytics
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Make data-driven decisions with confidence
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: '#003366',
                color: 'white',
                px: 6,
                py: 2,
                '&:hover': { bgcolor: '#002244' }
              }}
            >
              Get Started Today
            </Button>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default Analytics;
